(() => {
  if (window.__recentlyViewedProductsScriptInitialized) return;
  window.__recentlyViewedProductsScriptInitialized = true;

  const STORAGE_KEY = 'recentlyViewedProductHandles';
  const STORAGE_LIMIT = 20;
  const SECTION_SELECTOR = '[data-recently-viewed-products]';
  const GRID_SELECTOR = '[data-recently-viewed-grid]';
  const RAIL_SELECTOR = '[data-recently-viewed-rail]';
  const PREV_SELECTOR = '[data-recently-viewed-prev]';
  const NEXT_SELECTOR = '[data-recently-viewed-next]';
  const HIDDEN_CLASS = 'recently-viewed-products--hidden';
  const DESKTOP_BREAKPOINT = window.matchMedia('(min-width: 750px)');
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const CAROUSEL_EDGE_TOLERANCE = 4;
  const CAROUSEL_ANIMATION_DURATION = 300;

  const getStoredHandles = () => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];

      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];

      return parsed
        .filter((handle) => typeof handle === 'string' && handle.length > 0)
        .slice(0, STORAGE_LIMIT);
    } catch (error) {
      return [];
    }
  };

  const setStoredHandles = (handles) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles.slice(0, STORAGE_LIMIT)));
    } catch (error) {
      // localStorage may be unavailable in private mode or strict browser settings.
    }
  };

  const saveCurrentHandle = (currentHandle) => {
    if (!currentHandle) return;

    const handles = getStoredHandles().filter((handle) => handle !== currentHandle);
    handles.unshift(currentHandle);
    setStoredHandles(handles);
  };

  const fetchCardMarkup = async (handle, cardSectionId, routesRoot) => {
    const url = `${routesRoot}products/${encodeURIComponent(handle)}?section_id=${cardSectionId}`;

    try {
      const response = await fetch(url);
      if (!response.ok) return '';

      const htmlText = await response.text();
      const documentElement = new DOMParser().parseFromString(htmlText, 'text/html');
      const cardItem = documentElement.querySelector('.recently-viewed-product-card-item');
      return cardItem ? cardItem.outerHTML : '';
    } catch (error) {
      return '';
    }
  };

  const getVisibleCount = (sectionElement) => {
    const rawValue = DESKTOP_BREAKPOINT.matches
      ? sectionElement.dataset.visibleCountDesktop
      : sectionElement.dataset.visibleCountMobile;
    const visibleCount = Number.parseFloat(rawValue || '');

    if (Number.isNaN(visibleCount) || visibleCount <= 0) {
      return DESKTOP_BREAKPOINT.matches ? 4.2 : 2.2;
    }

    return visibleCount;
  };

  const getEffectiveVisibleCount = (sectionElement, itemCount) => {
    const visibleCount = getVisibleCount(sectionElement);

    if (!itemCount || itemCount <= 0) return visibleCount;

    return Math.max(1, Math.min(visibleCount, itemCount));
  };

  const getCarouselItems = (sectionElement) => {
    const gridElement = sectionElement.querySelector(GRID_SELECTOR);
    return gridElement ? Array.from(gridElement.children) : [];
  };

  const isGridLayoutSection = (sectionElement) => sectionElement.dataset.layoutMode === 'grid';

  const getCarouselPageSize = (sectionElement) => Math.max(1, Math.floor(getVisibleCount(sectionElement)));

  const getCarouselMaxScroll = (railElement) => Math.max(0, railElement.scrollWidth - railElement.clientWidth);

  const getClampedItemOffset = (itemElement, maxScrollLeft) => Math.min(itemElement.offsetLeft, maxScrollLeft);

  const getCarouselState = (sectionElement) => {
    if (!sectionElement.__recentlyViewedCarouselState) {
      sectionElement.__recentlyViewedCarouselState = {
        animationFrameId: null,
        settleTimeoutId: null,
        targetIndex: null,
      };
    }

    return sectionElement.__recentlyViewedCarouselState;
  };

  const clearCarouselSettleTimeout = (sectionElement) => {
    const state = getCarouselState(sectionElement);
    if (state.settleTimeoutId) {
      window.clearTimeout(state.settleTimeoutId);
      state.settleTimeoutId = null;
    }
  };

  const setCarouselTargetIndex = (sectionElement, index) => {
    const itemElements = getCarouselItems(sectionElement);
    const state = getCarouselState(sectionElement);
    const boundedIndex = itemElements.length ? Math.max(0, Math.min(index, itemElements.length - 1)) : 0;

    state.targetIndex = boundedIndex;
    return boundedIndex;
  };

  const getClosestCarouselIndex = (sectionElement) => {
    const railElement = sectionElement.querySelector(RAIL_SELECTOR);
    const itemElements = getCarouselItems(sectionElement);
    if (!railElement || !itemElements.length) return 0;

    const maxScrollLeft = getCarouselMaxScroll(railElement);
    const currentScrollLeft = railElement.scrollLeft;

    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    itemElements.forEach((itemElement, index) => {
      const targetLeft = getClampedItemOffset(itemElement, maxScrollLeft);
      const distance = Math.abs(targetLeft - currentScrollLeft);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    return closestIndex;
  };

  const finishCarouselAnimation = (sectionElement) => {
    const railElement = sectionElement.querySelector(RAIL_SELECTOR);
    const state = getCarouselState(sectionElement);

    if (state.animationFrameId) {
      window.cancelAnimationFrame(state.animationFrameId);
      state.animationFrameId = null;
    }

    clearCarouselSettleTimeout(sectionElement);

    if (railElement) {
      railElement.classList.remove('recently-viewed-products__rail--animating');
    }
  };

  const jumpCarouselToIndex = (sectionElement, index) => {
    const railElement = sectionElement.querySelector(RAIL_SELECTOR);
    const itemElements = getCarouselItems(sectionElement);
    if (!railElement || !itemElements.length) return;

    const boundedIndex = setCarouselTargetIndex(sectionElement, index);
    const maxScrollLeft = getCarouselMaxScroll(railElement);
    const targetLeft = getClampedItemOffset(itemElements[boundedIndex], maxScrollLeft);

    railElement.scrollLeft = targetLeft;
    updateCarouselButtons(sectionElement);
  };

  const animateCarouselToIndex = (sectionElement, index) => {
    const railElement = sectionElement.querySelector(RAIL_SELECTOR);
    const itemElements = getCarouselItems(sectionElement);
    if (!railElement || !itemElements.length) return;

    finishCarouselAnimation(sectionElement);

    const boundedIndex = setCarouselTargetIndex(sectionElement, index);
    const maxScrollLeft = getCarouselMaxScroll(railElement);
    const targetLeft = getClampedItemOffset(itemElements[boundedIndex], maxScrollLeft);
    const startLeft = railElement.scrollLeft;

    if (Math.abs(targetLeft - startLeft) <= 1 || REDUCED_MOTION.matches) {
      jumpCarouselToIndex(sectionElement, boundedIndex);
      return;
    }

    const state = getCarouselState(sectionElement);
    const startedAt = window.performance.now();

    railElement.classList.add('recently-viewed-products__rail--animating');

    const tick = (timestamp) => {
      const progress = Math.min((timestamp - startedAt) / CAROUSEL_ANIMATION_DURATION, 1);
      const easedProgress = 0.5 - Math.cos(progress * Math.PI) / 2;
      railElement.scrollLeft = startLeft + (targetLeft - startLeft) * easedProgress;
      updateCarouselButtons(sectionElement);

      if (progress < 1) {
        state.animationFrameId = window.requestAnimationFrame(tick);
        return;
      }

      finishCarouselAnimation(sectionElement);
      jumpCarouselToIndex(sectionElement, boundedIndex);
    };

    state.animationFrameId = window.requestAnimationFrame(tick);
  };

  const scheduleCarouselSettle = (sectionElement) => {
    const state = getCarouselState(sectionElement);
    if (state.animationFrameId) return;

    clearCarouselSettleTimeout(sectionElement);
    state.settleTimeoutId = window.setTimeout(() => {
      jumpCarouselToIndex(sectionElement, getClosestCarouselIndex(sectionElement));
      state.settleTimeoutId = null;
    }, 80);
  };

  const stopCarouselAnimation = (sectionElement) => {
    const state = getCarouselState(sectionElement);
    if (!state.animationFrameId) return;

    finishCarouselAnimation(sectionElement);
    jumpCarouselToIndex(sectionElement, getClosestCarouselIndex(sectionElement));
  };

  const stepCarousel = (sectionElement, direction) => {
    const state = getCarouselState(sectionElement);
    const currentIndex =
      state.animationFrameId && state.targetIndex !== null
        ? state.targetIndex
        : getClosestCarouselIndex(sectionElement);
    const nextIndex = currentIndex + getCarouselPageSize(sectionElement) * direction;

    animateCarouselToIndex(sectionElement, nextIndex);
  };

  const updateCarouselButtons = (sectionElement) => {
    const railElement = sectionElement.querySelector(RAIL_SELECTOR);
    const prevButton = sectionElement.querySelector(PREV_SELECTOR);
    const nextButton = sectionElement.querySelector(NEXT_SELECTOR);
    if (!railElement || !prevButton || !nextButton) return;

    const maxScrollLeft = getCarouselMaxScroll(railElement);
    const canScroll = maxScrollLeft > CAROUSEL_EDGE_TOLERANCE;
    const atStart = railElement.scrollLeft <= CAROUSEL_EDGE_TOLERANCE;
    const atEnd = railElement.scrollLeft >= maxScrollLeft - CAROUSEL_EDGE_TOLERANCE;

    prevButton.hidden = !canScroll || atStart;
    nextButton.hidden = !canScroll || atEnd;
  };

  const updateCarouselLayout = (sectionElement) => {
    const railElement = sectionElement.querySelector(RAIL_SELECTOR);
    const gridElement = sectionElement.querySelector(GRID_SELECTOR);
    if (!railElement || !gridElement) return;

    const itemElements = getCarouselItems(sectionElement);
    if (!itemElements.length) return;

    const state = getCarouselState(sectionElement);
    const currentIndex =
      state.animationFrameId && state.targetIndex !== null
        ? state.targetIndex
        : getClosestCarouselIndex(sectionElement);

    if (state.animationFrameId) {
      finishCarouselAnimation(sectionElement);
    }

    const visibleCount = getEffectiveVisibleCount(sectionElement, itemElements.length);
    const gap = Number.parseFloat(getComputedStyle(gridElement).gap || '0') || 0;
    const availableWidth = railElement.clientWidth;
    const gapCount = Math.max(0, Math.ceil(visibleCount) - 1);
    const cardWidth = (availableWidth - gapCount * gap) / visibleCount;

    if (cardWidth > 0) {
      sectionElement.style.setProperty('--recently-viewed-card-width', `${cardWidth}px`);
    }

    jumpCarouselToIndex(sectionElement, currentIndex);
  };

  const setupCarousel = (sectionElement) => {
    const railElement = sectionElement.querySelector(RAIL_SELECTOR);
    const prevButton = sectionElement.querySelector(PREV_SELECTOR);
    const nextButton = sectionElement.querySelector(NEXT_SELECTOR);
    if (!railElement) return;

    if (sectionElement.dataset.carouselBound !== 'true') {
      railElement.addEventListener(
        'scroll',
        () => {
          updateCarouselButtons(sectionElement);
          scheduleCarouselSettle(sectionElement);
        },
        { passive: true }
      );
      railElement.addEventListener('pointerdown', () => stopCarouselAnimation(sectionElement), { passive: true });
      railElement.addEventListener('touchstart', () => stopCarouselAnimation(sectionElement), { passive: true });
      railElement.addEventListener('wheel', () => stopCarouselAnimation(sectionElement), { passive: true });
      if (prevButton) {
        prevButton.addEventListener('click', () => stepCarousel(sectionElement, -1));
      }
      if (nextButton) {
        nextButton.addEventListener('click', () => stepCarousel(sectionElement, 1));
      }
      sectionElement.dataset.carouselBound = 'true';
    }

    updateCarouselLayout(sectionElement);
  };

  const renderSection = async (sectionElement, handles) => {
    const gridElement = sectionElement.querySelector(GRID_SELECTOR);
    if (!gridElement) return;

    const cardSectionId = sectionElement.dataset.cardSectionId;
    if (!cardSectionId) return;

    const limit = Number.parseInt(sectionElement.dataset.limit || '0', 10);
    if (Number.isNaN(limit) || limit <= 0) return;

    const uniqueHandles = [...new Set(handles)].slice(0, limit);
    if (!uniqueHandles.length) return;

    const routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const cardMarkups = await Promise.all(
      uniqueHandles.map((handle) => fetchCardMarkup(handle, cardSectionId, routesRoot))
    );
    const validMarkups = cardMarkups.filter(Boolean);

    if (!validMarkups.length) return;

    gridElement.innerHTML = validMarkups.join('');
    sectionElement.classList.remove(HIDDEN_CLASS);

    if (isGridLayoutSection(sectionElement)) return;

    setupCarousel(sectionElement);
  };

  const updateAllCarouselLayouts = () => {
    document.querySelectorAll(SECTION_SELECTOR).forEach((sectionElement) => {
      if (!sectionElement.classList.contains(HIDDEN_CLASS) && !isGridLayoutSection(sectionElement)) {
        updateCarouselLayout(sectionElement);
      }
    });
  };

  const initializeRecentlyViewed = () => {
    const sectionElements = Array.from(document.querySelectorAll(SECTION_SELECTOR));
    if (!sectionElements.length) return;

    const currentHandle = sectionElements[0].dataset.currentHandle;
    saveCurrentHandle(currentHandle);

    const handles = getStoredHandles().filter((handle) => handle !== currentHandle);
    sectionElements.forEach((sectionElement) => {
      renderSection(sectionElement, handles);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeRecentlyViewed);
  } else {
    initializeRecentlyViewed();
  }

  window.addEventListener('resize', updateAllCarouselLayouts);
  document.addEventListener('shopify:section:load', initializeRecentlyViewed);
})();
